import type {
  WhiteboardRecordTypeNames,
  RecordId,
  IntegrationRecordData,
  WhiteboardRecordModels,
  WhiteboardRecordInfo,
} from './shared-types';
import type WhiteboardController from './controllers/WhiteboardController';
import { type IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type EmbeddedTextBehaviors from './objects/EmbeddedTextBehaviorsV2';
import type DraggableItem from './DraggableItem';
import type ItemDragBehaviors from './objects/ItemDragBehaviors';
import { type useSingleton } from '@tippyjs/react';
import type Shape from './objects/Shape';
import type Connector from './objects/Connector';

export type MenuDirection = 'up' | 'down' | 'left' | 'right';

export type { RecordId };

type FayeMessage = Record<string, any>;

interface FayeSubscription {
  cancel: () => void;
}

export interface FayeClient {
  subscribe: <T extends FayeMessage>(
    channel: string,
    handler: (message: T) => void
  ) => FayeSubscription;

  publish: (channel: string, message: FayeMessage) => void;
}

export interface UserInfo {
  displayName: string;
  avatar?: string;
}
export type ResolveUserInfo = (id: RecordId) => Promise<UserInfo>;

export interface CursorUser extends UserInfo {
  isLoaded?: boolean;
}

export interface CursorPosition {
  userId: RecordId;
  cursorId: number;
  x?: number;
  y?: number;
  color?: string;
  showNameAt?: number;
  receivedAt?: number;
  forceShowName?: boolean;
  hideName?: boolean;
  userInfo: CursorUser;
}

export interface CursorMessage {
  cursorId: number;
  userId: RecordId;
  x: number;
  y: number;
}

export interface ControlledObject {
  controller: WhiteboardController;
}

export interface RecordReference {
  recordType: WhiteboardRecordTypeNames;
  recordId: RecordId;
}

export const creatableRecordTypes = [
  'ideas',
  'features',
  'requirements',
  'epics',
  'goals',
  'initiatives',
  'pdlcSteps',
  'releases',
] as const;

export type CreatableRecordType = (typeof creatableRecordTypes)[number];

export type SmartRecordCreatableType = Exclude<
  CreatableRecordType,
  'pdlcSteps'
>;

export interface CreatedRecordMetadata {
  recordId: RecordId;
  id: RecordId;
}

/**
 * When creating a record this is the parent, a project or release.
 */
export type CreateParent =
  | {
      id: RecordId;
      name: string;
    }
  | { id: null; name: null };

// Identity the start and end of a connector.
export enum ConnectorEnd {
  CONNECTOR_START = 0,
  CONNECTOR_END = 1,
}

// The name of an attachment point.
export enum AttachmentPoint {
  Left = 'cl',
  Right = 'cr',
  Top = 'ct',
  Bottom = 'cb',
  Center = 'cc',
  Edge = 'ce',
}

/** Attachment points which are visible on the canvas */
export type VisibleAttachmentPoint = Exclude<
  AttachmentPoint,
  AttachmentPoint.Edge | AttachmentPoint.Center
>;

export interface Connectable {
  attachmentPoints(): AttachmentPoint[];
  connectControlDefinition(): ConnectControlDefinition;
}

export interface HasDraggableItems<T extends { id: RecordId }> {
  itemBorderRadius: number;
  itemCount: () => number;
  componentOffset: () => fabric.IPoint;
  draggableItems: ({
    draggingVertical,
    scaled,
  }: {
    draggingVertical?: boolean;
    scaled?: boolean;
  }) => DraggableItem<T>[];
  draggableItemForId: ({
    id,
    draggingVertical,
    scaled,
  }: {
    id: RecordId;
    draggingVertical?: boolean;
    scaled?: boolean;
  }) => DraggableItem<T> | undefined;
  updateItemOrder: ({ id }: { id: RecordId }) => boolean;
}

export type DraggableItemContainer = fabric.Object &
  HasDraggableItems<any> &
  ItemDragBehaviors;

export interface HasEmbeddedText {
  calcTextMargin(): {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
  selectedTextFields: (fabric.Object & EmbeddedTextBehaviors)[];
  preventTextEditing?: () => boolean;
}

// For shapes that conform to constrained resizing with min/max dimensions.
export interface Resizable {
  minDimension: ({ resizingWidth }: { resizingWidth: boolean }) => number;
  maxDimension: ({ resizingWidth }: { resizingWidth: boolean }) => number;
}
// Generic construtor used by mixins.
export type Constructor<T = {}> = new (...args: any[]) => T;

export interface Interval {
  startOfInterval: string;
  daysCount: number;
}

export interface FormatableInterval {
  intervals: [Interval];
  format: (date: string, type: string) => string;
  totalDays: number;
}

// Interval to be renedered as part of a timeline component.
export interface TimelineInterval {
  intervalType: string;
  interval: FormatableInterval;
}
// Definiton of where connectors can originate from a shape.
export interface ControlOffset {
  x: number;
  y: number;
}

export type ConnectControlDefinition<T = {}> = Partial<
  Record<AttachmentPoint | 'aux1' | 'aux2', ControlOffset & T>
>;

export type ConnectControlWithNormalDefinition = ConnectControlDefinition<{
  normal: { x: number; y: number };
  oppositeAttachmentPoint?: AttachmentPoint;
}>;

// Shape Timeline render setings
export interface TimelineRenderSettings {
  offset: number;
  startDate: string;
  endDate: string;
  visibleStartDate: string;
  visibleEndDate: string;
  hideWeekends: boolean;
  timescale: string;
  fiscalMonth: number;
  fiscalYearFormat: string;
}

export interface IconData {
  prefix: string;
  iconName: string;
  synonyms: string[][] | string[];
  icon: IconDefinition['icon'];
}

export interface SpriteSvgItem {
  path: string;
  viewBox: string;
}

// either a single string referencing the iconDb, or an svg string
export type IconReference = string | { type: 'svg'; svg: string };
export type IconWithOffset =
  | IconReference
  // icon name/svg, x offset, y offset
  | [IconReference, number]
  | [IconReference, number, number];
export interface NormalizedIcon {
  offset: { x: number; y: number };
  reference: IconReference;
}
// alias to make it clear this is an image that has already been decoded
type DecodedImageElement = HTMLImageElement;
export type PreparedIcon = {
  offset: { x: number; y: number };
  width: number;
  height: number;
} & (
  | {
      type: 'svg';
      img: DecodedImageElement;
    }
  | {
      type: 'path';
      path: IconDefinition['icon'][4];
    }
);

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Bounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type SVGCommand = [string, ...number[]];

export type SingletonObject = ReturnType<typeof useSingleton>[0];

export interface IntegrationRecord {
  recordType: string;
  recordKey: string;
  createdAt: string; // ISO8601 timestamp
  updatedAt?: string; // ISO8601 timestamp
  recordUrl: string; // should be present going forward, but may be missing in older shapes
  recordData?: IntegrationRecordData;
}

export type IntegrationShape = Shape & { integrationRecord: IntegrationRecord };

export type WhiteboardRecordWithRefNum = WhiteboardRecordInfo & {
  referenceNum?: string | null;
};
export interface RecordLinkItem {
  parentId: RecordId;
  childId: RecordId;
  parentType: WhiteboardRecordTypeNames | WhiteboardRecordModels['typename'];
  childType: WhiteboardRecordTypeNames | WhiteboardRecordModels['typename'];
  connector: Connector;
  parentRecord?: WhiteboardRecordWithRefNum;
  childRecord?: WhiteboardRecordWithRefNum;
}

// Creates a union of all keys of all objects in the union
type GetKeys<U> = U extends any ? keyof U & (string | number | symbol) : never;

type AddKeys<U, Keys extends string | number | symbol> = U & {
  [K in Exclude<Keys, keyof U>]?: undefined;
};

/**
 * Helper type to allow property access checks on a union type as per standard JS,
 * without additional in checks
 * e.g. given
 *
 * type T = { a: string } | { b: number };
 *
 * const t: T = {...};
 * t.a // Error, cos it doesn't exist in { b: number }
 * 'a' in t ? t.a : undefined // Would need to do this
 *
 * const t2: DistributeUnion<T> = {...};
 * t2.a // Works, result is string | undefined as expected
 *
 * Based on https://github.com/microsoft/TypeScript/issues/1260#issuecomment-1288111146
 *
 */
export type DistributeUnion<
  U,
  Keys extends GetKeys<U> = GetKeys<U>
> = U extends any ? AddKeys<U, Keys> : never;

/**
 * Omit first entry from a tuple. Useful for partial function application, e.g.
 *
 * const add = (x: number, y: number) => x + y;
 * const add42 = (...args: OmitFirst<Parameters<typeof add>>) => add(42, ...args);
 */
export type OmitFirst<T extends unknown[]> = T extends [any, ...infer U]
  ? U
  : never;

export type HierarchyImportRelationshipType =
  | 'parents'
  | 'children'
  | 'dependencies'
  | 'related';

export interface HierarchyNode {
  record: RecordReference;
  parentIds: string[];
  childIds: string[];
  depth: number;
  relationshipType: HierarchyImportRelationshipType;
  linkType?: string;
  relationDescription?: string;
}

export interface HierarchyResult {
  nodes: Record<string, HierarchyNode>;
  originalRecordId: RecordId;
}

export interface FetchOptions {
  maxRecords: number;
  alreadyFetched: Set<string>;
}

export interface RelatedRecordManager {
  supportedRecordTypes: string[];
  fetchHierarchy(
    recordId: string,
    recordType: any,
    relationshipType: HierarchyImportRelationshipType,
    options?: FetchOptions
  ): Promise<HierarchyResult>;
}

export interface RecordLinkOption {
  value: Aha.RecordLinkAttributes['linkType'];
  text: string;
  inverse: boolean;
}

export interface RecordLinkInfo {
  parentRecord: {
    id: string;
    type: string;
  };
  childRecord: {
    id: string;
    type: string;
  };
}

export interface RecordLinkManager {
  supportedRecordLinkTypes: () => string[];
  supportedRecordLinkOptions: () => RecordLinkOption[];
  isLinkableRecordType: (recordType: string) => boolean;
  createRecordLink({
    parentRecord,
    childRecord,
    linkType,
  }: {
    parentRecord: { id: string; type: string };
    childRecord: { id: string; type: string };
    linkType: Aha.RecordLinkAttributes['linkType'];
  }): Promise<{
    result: boolean;
    recordLink: Aha.RecordLink;
  }>;
  createRecordLinks({
    items,
    linkType,
  }: {
    items: RecordLinkInfo[];
    linkType: string;
  }): Promise<{
    result: boolean;
    recordLinks: Aha.RecordLink[];
  }>;
}
